import { Injectable } from "@angular/core";
import {
  IDataSourceBackendParam,
  IDevicesAvailableToOrder,
  IDevicesOrder,
  INewPrescription,
  IOfficialCareplanDetails,
  IPractitionerTemplate,
  IPrescription,
  IPrescriptionInfosResponse,
  IPrescriptionToBeRenewedInfosResponse,
  IPricingConfig,
  OnlineDevice,
  PRESCRIPTION_TYPE,
  RENEW_TIMING,
  TemplateDocCategory,
} from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { PrescriptionApiService } from "./api/prescription-api.service";

@Injectable({
  providedIn: "root",
})
export class PrescriptionService {
  constructor(private prescriptionApiService: PrescriptionApiService) {}

  public getPendingPrescriptionInfos(
    params: IDataSourceBackendParam & {
      organizations?: string;
      patientCaremateId?: string;
      period?: string;
      date?: string;
      status?: string;
    }
  ): Observable<IPrescriptionInfosResponse[]> {
    return this.prescriptionApiService.getPrescriptionInfos(params) as Observable<IPrescriptionInfosResponse[]>;
  }

  public getPendingPrescriptionCount(param: {
    patientCaremateId?: string;
    services?: string[];
    organizations?: string[];
    period?: string;
    date?: string;
    status?: number[];
  }): Observable<{
    count: number;
  }> {
    return this.prescriptionApiService.getPrescriptionInfosCount(
      param.patientCaremateId,
      param.services,
      param.organizations,
      param.period,
      param.date,
      param.status
    );
  }

  public acceptPrescription(prescriptionId: string, careplanId?: string): Observable<any> {
    return this.prescriptionApiService.acceptPrescription(prescriptionId, careplanId) as Observable<IPrescriptionInfosResponse[]>;
  }
  public listPrescriptionsToBeRenewedInfos(
    params: IDataSourceBackendParam & { timing: RENEW_TIMING; services?: string }
  ): Observable<IPrescriptionToBeRenewedInfosResponse[]> {
    return this.prescriptionApiService.getPrescriptionsToBeRenewedInfos(params.timing, params.pageNumber, params.pageSize, params.services);
  }
  public getPrescriptionsToBeRenewedCount(
    timing: RENEW_TIMING,
    services?: string[]
  ): Observable<{
    count: number;
  }> {
    return this.prescriptionApiService.getPrescriptionsToBeRenewedCount(timing, services);
  }

  public createPrescription(prescription: INewPrescription, type: PRESCRIPTION_TYPE): Observable<IPrescription> {
    return this.prescriptionApiService.createPrescription(prescription, type);
  }

  public getPrescriptionsOfficialCareplan(careplanId: string): Observable<IOfficialCareplanDetails> {
    return this.prescriptionApiService.getPrescriptionsOfficialCareplan(careplanId);
  }

  public getDevicesToOrder(type: OnlineDevice, externalRessourceRefs?: string[]): Observable<IDevicesAvailableToOrder[]> {
    return this.prescriptionApiService.getDevicesToOrder(type, externalRessourceRefs);
  }

  public orderDevice(
    patientId: string,
    orderName: string,
    estimatedDays: number,
    city: string,
    country: string,
    streetName: string,
    streetNumber: string,
    postalCode: string,
    devicesSetName: string,
    devicesSetTypes: string[],
    devicesBrand: OnlineDevice,
    boxNumber?: string,
    additionalStuff?: number,
    externalRessourceRef?: string
  ): Observable<IDevicesOrder> {
    return this.prescriptionApiService.orderDevice(
      patientId,
      orderName,
      estimatedDays,
      city,
      country,
      streetName,
      streetNumber,
      postalCode,
      devicesSetName,
      devicesSetTypes,
      devicesBrand,
      boxNumber,
      additionalStuff,
      externalRessourceRef
    );
  }

  public getPrescriptionTemplates(service: string): Observable<IPractitionerTemplate[]> {
    return this.prescriptionApiService.getPrescriptionTemplates(service);
  }

  public createPrescriptionTemplates(
    file: any,
    practitionerId: string,
    type: TemplateDocCategory,
    availableToService = false
  ): Observable<IPractitionerTemplate> {
    return this.prescriptionApiService.createPrescriptionTemplates(file, practitionerId, type, availableToService);
  }

  public deletePrescriptionTemplate(identifier: string): Observable<void> {
    return this.prescriptionApiService.deletePrescriptionTemplate(identifier);
  }

  public autoCompletePrescriptionTemplate(templateId: string, patientId: string, lang: string, duration: number): Observable<Blob> {
    return this.prescriptionApiService.autoCompletePrescriptionTemplate(templateId, patientId, lang, duration) as Observable<Blob>;
  }

  public getPrescription(prescriptionId: string): Observable<IPrescription> {
    return this.prescriptionApiService.getPrescription(prescriptionId) as Observable<IPrescription>;
  }

  public getPrescriptionConfig(prescriptionId: string, lang: string): Observable<IPricingConfig> {
    return this.prescriptionApiService.getPrescriptionConfig(prescriptionId, lang) as Observable<IPricingConfig>;
  }

  public editPrescription(prescription: IPrescription, config: IPricingConfig): Observable<IPrescription> {
    return this.prescriptionApiService.editPrescription(prescription, config);
  }
}
